import React, { useContext, useState } from 'react';

import { StudentApiContext } from '../api/StudentApiContext';
import { useLoader } from '../api/useLoader';
import Button from '../components/button';
import Spinner from '../components/spinner/spinner';
import { getISOWeek } from 'date-fns';
import { useIntl } from 'react-intl';
import translations from '../practiceactivity/activity.student.i18n';
import {
  PraksisaktivitetStatuserDto,
  PraksisoversiktEmneDto,
} from '../api/generated/student-generated-api';
import { getStudyYearForDate } from '../date.utils';
import * as css from './activities.module.scss';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import { useNavigate } from 'react-router-dom';

const Activites = () => {
  const navigate = useNavigate();
  const { praksisApi } = useContext(StudentApiContext);

  const [activeCol, setActiveCol] = useState<number>(-1);
  const [tableRows, setTableRows] = useState<JSX.Element[]>();

  const now = new Date(Date.now());

  const [fromDate] = getStudyYearForDate(now);

  const { data, loading } = useLoader(() =>
    praksisApi.praksisGet({
      queryParams: { fromDate: fromDate.getTime() },
    }),
  );

  const { formatMessage, locale } = useIntl();

  const labels = {
    thisWeek: formatMessage({ id: translations.thisWeek.id }),
    week: formatMessage({ id: translations.week.id }),
    spring: formatMessage({ id: translations.spring.id }),
    autumn: formatMessage({ id: translations.autumn.id }),
    showActivity: formatMessage({ id: translations.showActivity.id }),
    currentActivities: formatMessage({ id: translations.currentActivities.id }),
    emptyActivities: formatMessage({ id: translations.activitiesEmpty.id }),
    thisWeekToWeek: formatMessage({ id: translations.thisWeekToWeek.id }),
  };

  const translateStatus = (status: string) => {
    return formatMessage({ id: translations[`status${status}`].id });
  };

  const period = (
    from: string,
    to: string,
    status: PraksisaktivitetStatuserDto,
  ) => {
    const fromDate = new Date(from);
    const fromWeek = getISOWeek(fromDate);
    const toWeek = getISOWeek(new Date(to));
    const thisWeek = getISOWeek(now);
    const fromMonth = fromDate.getMonth();
    const fromYear = fromDate.getFullYear();
    const thisYear = now.getFullYear();

    let result = '';
    switch (status) {
      case PraksisaktivitetStatuserDto.Undervisningsmeldt: {
        result = fromMonth <= 6 ? labels.spring : labels.autumn;
        break;
      }
      case PraksisaktivitetStatuserDto.Registrert:
      case PraksisaktivitetStatuserDto.OnskelisteTilgjengelig:
      case PraksisaktivitetStatuserDto.OnskelisteRegistrert:
      case PraksisaktivitetStatuserDto.OnskelisteRegistrertIkkeTilgjengelig:
      case PraksisaktivitetStatuserDto.IkkePublisert:
      case PraksisaktivitetStatuserDto.PraksisplassMottatt:
      case PraksisaktivitetStatuserDto.Gjennomfoert: {
        result = `${labels.week} ${
          fromWeek !== toWeek ? `${fromWeek}-${toWeek}` : fromWeek
        }`;
        break;
      }
      case PraksisaktivitetStatuserDto.Aktiv: {
        if (thisYear !== fromYear) {
          result = `${labels.week} ${fromWeek}-${toWeek}`;
        } else if (fromWeek === toWeek) {
          result = labels.thisWeek;
        } else if (fromWeek === thisWeek) {
          result = `${
            labels.thisWeekToWeek
          } ${labels.week.toLowerCase()} ${toWeek}`;
        }
        break;
      }
      default:
        break;
    }

    return result + ' ' + fromYear;
  };

  const headers = [
    { title: formatMessage({ id: translations.subject.id }) },
    { title: formatMessage({ id: translations.status.id }) },
    { title: formatMessage({ id: translations.period.id }) },
    { title: formatMessage({ id: translations.action.id }) },
  ];

  const getRows = (sortedOversikt?: PraksisoversiktEmneDto[]) => {
    return (sortedOversikt || data!.emner).map((emne, index) => {
      let tabHeader = 0;

      return (
        <TableRow key={index}>
          <TableCellTd header={headers[tabHeader++].title}>
            {emne.emne}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {translateStatus(emne.status)}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {period(emne.startDato, emne.sluttDato, emne.status)}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader].title}>
            <>
              {emne.status !== PraksisaktivitetStatuserDto.Undervisningsmeldt &&
                emne.praksissaktivitet && (
                  // TODO: Ideally this should not be a button, but a NavLink
                  <Button.Secondary
                    onClick={() =>
                      navigate('/activity/' + emne.praksissaktivitet)
                    }
                  >
                    {labels.showActivity}
                  </Button.Secondary>
                )}
            </>
          </TableCellTd>
        </TableRow>
      );
    });
  };

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = (
    col: number,
    a: PraksisoversiktEmneDto,
    b: PraksisoversiktEmneDto,
  ) => {
    switch (col) {
      case 0:
        return compare(a.emne, b.emne);
      case 1:
        return compare(translateStatus(a.status), translateStatus(b.status));
      case 2:
        return compare(
          period(a.startDato, a.sluttDato, a.status),
          period(b.startDato, b.sluttDato, b.status),
        );
    }
    return 0;
  };

  const sortActivitiesByActiveCol = (col: number, ascending: boolean) => {
    setTableRows(
      getRows(
        data?.emner.sort((a, b) =>
          ascending ? sort(col, b, a) : sort(col, a, b),
        ),
      ),
    );
  };

  const canSortIndices = [0, 1, 2];

  const getHeaders = () =>
    headers.map(({ title }, index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeCol}
        setActiveCol={setActiveCol}
        sort={sortActivitiesByActiveCol}
        canSort={canSortIndices.includes(index)}
      >
        {title}
      </TableCellTh>
    ));

  return (
    <>
      <div className={css.space40} />
      <div className={css.activities}>
        <h2>{labels.currentActivities}</h2>
        {loading && <Spinner />}
        {data && (
          <>
            <Table>
              <TableHeader>
                <TableRow top>{getHeaders()}</TableRow>
              </TableHeader>
              <TableMain>{tableRows || getRows()}</TableMain>
            </Table>
            {data.emner.length === 0 && <p>{labels.emptyActivities}</p>}
          </>
        )}
      </div>
      <div className={css.space40} />
    </>
  );
};

export default Activites;
